import React from 'react';
import { alpha, Box, ButtonBase, CircularProgress, IconButton, Link, Stack, Tooltip, Typography } from '@mui/material';
import { HelpOutline, Storefront } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

export const BOOK_MODE_ARTICLE = '/research/10-book-mode';

interface BookModeToggleProps {
  on: boolean;
  onChange: (on: boolean) => void;
  size?: 'small' | 'medium';
  /** Show the ? with the explanation and a link to the article. */
  help?: boolean;
}

interface OnOffPillProps {
  on: boolean;
  onChange: (on: boolean) => void;
  label: string;
  icon: typeof Storefront;
  size?: 'small' | 'medium';
  disabled?: boolean;
  /** Shows a spinner in place of the icon. */
  loading?: boolean;
}

/** On/off pill styled like the site's segmented toggles (Book mode, Momentum). */
export const OnOffPill: React.FC<OnOffPillProps> = ({ on, onChange, label, icon: Icon, size = 'medium', disabled, loading }) => {
  const height = size === 'small' ? 26 : 32;
  return (
    <Box
      sx={(theme) => ({
        display: 'inline-flex',
        p: '3px',
        borderRadius: 999,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.03)',
      })}
    >
      <ButtonBase
        role="switch"
        aria-checked={on}
        aria-label={label}
        onClick={() => onChange(!on)}
        disabled={disabled}
        sx={(theme) => ({
          height,
          px: size === 'small' ? 1.25 : 1.75,
          gap: 0.75,
          borderRadius: 999,
          fontSize: size === 'small' ? '0.75rem' : '0.8125rem',
          fontWeight: 600,
          letterSpacing: '0.02em',
          whiteSpace: 'nowrap',
          transition: theme.transitions.create(['background-color', 'color', 'box-shadow'], { duration: 150 }),
          color: on ? (theme.palette.mode === 'dark' ? '#0d1f12' : theme.palette.primary.contrastText) : theme.palette.text.secondary,
          bgcolor: on ? theme.palette.primary.main : 'transparent',
          boxShadow: on ? `0 0 12px ${alpha(theme.palette.primary.main, 0.35)}` : 'none',
          '&:hover': {
            color: on ? undefined : theme.palette.text.primary,
            bgcolor: on ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.1),
          },
          '&.Mui-disabled': { opacity: 0.45 },
          '&.Mui-focusVisible': { outline: `2px solid ${alpha(theme.palette.primary.main, 0.6)}`, outlineOffset: 1 },
        })}
      >
        {loading ? (
          <CircularProgress size={size === 'small' ? 13 : 15} color="inherit" />
        ) : (
          <Icon sx={{ fontSize: size === 'small' ? 15 : 17 }} />
        )}
        {label}
        <Box
          component="span"
          sx={(theme) => ({
            fontSize: '0.7rem',
            fontWeight: 700,
            // Fixed width (fits "OFF") so the pill doesn't change size between ON and OFF.
            width: '3.2em',
            textAlign: 'center',
            transition: 'background-color 150ms',
            borderRadius: 999,
            bgcolor: on ? alpha('#000', 0.15) : alpha(theme.palette.text.primary, 0.08),
          })}
        >
          {on ? 'ON' : 'OFF'}
        </Box>
      </ButtonBase>
    </Box>
  );
};

/** On/off pill for book mode (styled like the site's segmented toggles), with an optional ? explainer. */
export const BookModeToggle: React.FC<BookModeToggleProps> = ({ on, onChange, size = 'medium', help = true }) => {
  return (
    <Stack direction="row" alignItems="center" spacing={0.25} sx={{ flexShrink: 0 }}>
      <OnOffPill on={on} onChange={onChange} label="Book mode" icon={Storefront} size={size} />
      {help && (
        <Tooltip
          enterTouchDelay={0}
          leaveDelay={200}
          slotProps={{ tooltip: { sx: { maxWidth: 320, p: 1.5 } } }}
          title={
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Book mode
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Shows our numbers the way a sportsbook would price them: the field flattened (favorites a little less
                likely, longshots more likely) with a typical book margin added. Compare it with the real board to spot prices
                that are out of line. The value calculator always uses our own probabilities.
              </Typography>
              <Link component={RouterLink} to={BOOK_MODE_ARTICLE} underline="hover" sx={{ fontWeight: 600, color: 'primary.main' }}>
                Learn more: how to use book mode →
              </Link>
            </Box>
          }
        >
          <IconButton size="small" aria-label="About book mode">
            <HelpOutline fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
};
